import { useLocalSearchParams, useNavigation } from 'expo-router'
import { useEffect } from 'react'
import { Platform } from 'react-native'
import { useCreateCustomer } from '@/hooks/customers'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useCustomerForm } from '@/hooks/useCustomerForm'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { errorToString } from '@/lib/errors'
import { useReportNewlyCreated } from '@/lib/newlyCreatedRecord'
import { notify } from '@/lib/notice'
import { sanitizeReturnTo } from '@/lib/returnTo'
import type { CustomerFormData } from '@/types/customers'
import { Button } from '@/ui/Button'
import { CustomerFormFields } from '@/ui/customers/CustomerFormFields'
import { FormErrorSummary } from '@/ui/form'
import { Page } from '@/ui/Page'

export default function NewCustomerPage() {
    useAvoidSoftInputForScreen()
    const params = useLocalSearchParams<{ orgSlug: string; returnTo?: string }>()
    const returnTo = sanitizeReturnTo(params.returnTo, params.orgSlug)
    const { navigateBackTo } = useNavigateBack()
    const { mutation, isCreating } = useCreateCustomer()
    const reportNewlyCreated = useReportNewlyCreated()
    const navigation = useNavigation()

    useEffect(() => {
        if (returnTo && Platform.OS !== 'ios') {
            navigation.setOptions({
                headerLeft: () => (
                    <Button
                        icon="chevron-left"
                        title="Back"
                        onPress={() => navigateBackTo(returnTo)}
                    />
                ),
            })
        }
    }, [returnTo, navigation, navigateBackTo])

    const {
        control,
        handleSubmit,
        setError,
        formState: { errors, isSubmitted, isSubmitting },
    } = useCustomerForm({
        onSubmit: async (data: CustomerFormData) => {
            try {
                const result = await mutation.mutateAsync(data)
                notify.success('Customer created successfully')

                if (returnTo) {
                    reportNewlyCreated('customers', result.customerId)
                    navigateBackTo(returnTo)
                } else {
                    navigateBackTo(`/${params.orgSlug}/settings/customers`)
                }
            } catch (error) {
                const message = errorToString(error)
                setError('name', { type: 'manual', message })
            }
        },
    })

    useHeaderOptions({
        buttons: {
            type: 'create-save',
            onPress: handleSubmit,
            loading: isSubmitting || isCreating,
            loadingTitle: 'Creating...',
        },
    })

    return (
        <Page>
            <FormErrorSummary errors={errors} isEnabled={isSubmitted} />
            <CustomerFormFields control={control} />
        </Page>
    )
}
